
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js/auto';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../framework/state';

import {
    barChartOptions,
    chartBackgroundColors,
    getMonthLabelsNew,
    getTitleOptions,
    numberTooltipCallbacks,
} from '../../../service/report_service';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import { formatMonthStrings } from '../../../../../common/framework/util/time';
import { getMonthlyNumbersVehicleContracts } from '../../../client/vehicle_contract_client';

@Component
export default class MonthlyVehicleContractsChart extends Vue {
    readonly resourceType = ApplicationResource.VEHICLE_CONTRACT;

    @Prop(Array) readonly customerIds!: string[];
    @Prop(Date) readonly lastMonth!: Date;

    shared = sharedState;

    local = {
        monthlyVehicleContractsChart: undefined as Chart | undefined,
        chartData: {} as {
            monthlyContracts: { month: string; value: number }[];
        },
        months: [] as string[],
        loading: false,
    };

    @Watch('customerIds')
    @Watch('lastMonth')
    async inputsChanged() {
        if (this.local.loading) {
            return;
        }

        await this.loadAsyncData();
        this.addMonthlyVehicleContractsChart();
    }

    private async loadAsyncData() {
        this.local.loading = true;

        const beginDate = DateTime.fromJSDate(this.lastMonth || new Date())
            .minus({ months: 11 })
            .toJSDate();
        this.local.months = formatMonthStrings(beginDate.getFullYear(), beginDate.getMonth(), 11);

        const parameters = new Map<string, any>([['customerId', this.customerIds]]);

        const specialQueryParameters = new Map<string, any>();

        if (this.local.months) {
            specialQueryParameters.set('months', this.local.months);
        }

        const customerIds = parameters.get('customerId');

        if (customerIds && Array.isArray(customerIds) && customerIds.length > 0) {
            const monthlyContracts = await getMonthlyNumbersVehicleContracts(parameters, specialQueryParameters);
            this.local.chartData = { monthlyContracts };
        }

        this.local.loading = false;
    }

    private addMonthlyVehicleContractsChart() {
        const canvasContext = (document.getElementById(
            'monthly-vehicle-contracts-chart',
        ) as HTMLCanvasElement)!!.getContext('2d')!!;

        const chartConfiguration: ChartConfiguration = {
            type: 'bar',
            data: {
                labels: getMonthLabelsNew(this, this.local.months),
                datasets: this.chartDataSets,
            },
            options: {
                ...barChartOptions,
                plugins: {
                    ...barChartOptions.plugins,
                    title: getTitleOptions(`${this.$t('title.monthlyVehicleContracts')}`),
                    tooltip: {
                        callbacks: { ...numberTooltipCallbacks },
                    },
                },
                scales: {
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        suggestedMin: 0,
                        ticks: {
                            precision: 0,
                        },
                    },
                },
            },
        };

        this.local.monthlyVehicleContractsChart?.destroy();
        this.local.monthlyVehicleContractsChart = new Chart(canvasContext, chartConfiguration);
    }

    private static get backgroundColors(): string[] {
        return chartBackgroundColors;
    }

    private get chartDataSets(): ChartDataset[] {
        const dataSets: ChartDataset[] = [
            {
                label: `${this.$t('label.totalContracts')}`,
                data: [],
                type: 'line',
                borderColor: MonthlyVehicleContractsChart.backgroundColors[1],
                backgroundColor: MonthlyVehicleContractsChart.backgroundColors[1],
                fill: false,
            },
            // {
            //     label: `${this.$t('label.activeContracts')}`,
            //     data: [],
            //     type: 'line',
            //     borderColor: MonthlyVehicleContractsChart.backgroundColors[2],
            //     backgroundColor: MonthlyVehicleContractsChart.backgroundColors[2],
            //     fill: false,
            // },
        ];

        this.local.months.forEach((month, index) => {
            const chartDataToUse = this.local.chartData;

            const contractsNumber = chartDataToUse.monthlyContracts.find(
                (number: { month: string; value: number }) => number.month === month,
            )?.value;

            if (dataSets[0] && dataSets[0].data) {
                dataSets[0].data.push(contractsNumber ? contractsNumber : 0);
            }

            // const activeContractsNumber = chartDataToUse.monthlyActiveContracts.find(
            //     (number: { month: string; value: number }) => number.month === month,
            // )?.value;

            // if (dataSets[1] && dataSets[1].data) {
            //     dataSets[1].data.push(activeContractsNumber ? activeContractsNumber : 0);
            // }
        });

        return dataSets;
    }
}
